import { createTheme } from '@mui/material/styles';
import { useEffect, useState } from "react";
import { Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, Grid, IconButton, LinearProgress, Snackbar, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography, useMediaQuery } from "@mui/material";
import ISala from '../../types/Sala';
import SelectorClases from './SelectorClases';
import LogService from "../../services/LogService";
import ILog from "../../types/Log";
import IMasterClass from "../../types/MasterClass"
import ClaseUtils from "../../utils/Clase";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import IActividad from '../../types/Actividad';
import ConfigUtils from '../../utils/Config';
import IPaciente from '../../types/Paciente';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import * as constantes from "../../common/Constantes";
import IAlert from '../../types/Alert';
import AgendaService from '../../services/AgendaService';
import { useNavigate } from "react-router-dom";
import moment, { Moment } from 'moment';
import { aFecha, esFechaValida } from '../../utils/FechaSelector';
import Utilities from '../../common/Utilities';
import IListaEspera from '../../types/ListaEspera';

const locale = constantes.locale;

type PacienteActivitiesProps = {
    masterClasses: IMasterClass[],
    pacientes: IPaciente[],
    onClose: () => void,
    // El padre aplica en el momento las clases que devuelve la API (si no, un segundo cambio hecho antes de que termine la
    // recarga enviaría la lista de pacientes antigua y borraría el anterior) y después recarga el calendario
    callBack: (actualizadas?: IMasterClass[]) => void,
    onError: (mensaje: string) => void, // la alerta la pinta el padre
    masterIds?: string[], // desde la ficha de una clase: las clases ya elegidas (ella o ella y su pareja), sin el paso de selección
    sesionesPorClase?: Record<string, Date[]> // fechas de las sesiones de cada clase (para mostrar su primera clase real)
}

// Día elegido en un selector de fecha: inicio o final del día local, o una fecha no válida si el campo está vacío o a medio
// escribir (antes se ignoraba: el campo se veía vacío o a medias pero se guardaba la fecha anterior). Se valida al guardar.
const diaDelSelector = (date: Moment | null, extremo: 'inicio' | 'fin'): Date => {
    const fecha = aFecha(date);
    if (!fecha) return new Date(NaN);
    return extremo === 'inicio' ? moment(fecha).startOf('day').toDate() : moment(fecha).endOf('day').toDate();
};

const PacienteActivities = (props: PacienteActivitiesProps) => {
    const theme = createTheme();
    // Móvil: los diálogos grandes de la sección ocupan toda la pantalla para poder trabajar con todo el ancho
    const pantallaMovil = useMediaQuery(theme.breakpoints.down('sm'));
    const history = useNavigate();

    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState<IAlert>({ open: false, severity: 'info', msg: '' });
    const [actividades] = useState<Array<IActividad>>(ConfigUtils.getActividades());

    const [checked, setChecked] = useState<Array<string>>(props.masterIds ?? []);
    const [pacientesIdsMultiple, setPacientesIdsMultiple] = useState<Array<Array<IPaciente>>>([]);

    // --- MODAL ADD PACIENTES ---
    const initialAddState = {
      checked: [] as string[],
      startClass: new Date(),
      endClass: new Date(),
      allClasses: "Todas las clases",
      listaEspera: [] as IListaEspera[],
      verListaEspera: false
    };
    const [modalAddPacientes, setModalAddPacientes] = useState(false);
    const [modalAddState, setModalAddState] = useState(initialAddState);

    // --- MODAL EDIT PACIENTE ---
    const initialEditState = {
      pacienteToEdit: undefined as IPaciente | undefined,
      masterToEdit: undefined as IMasterClass | undefined,
      tramoToEdit: undefined as IMasterClass['pacientes'][number] | undefined, // el tramo de la fila pulsada
      startClassEdit: new Date(),
      endClassEdit: new Date()
    };
    const [modalEditPaciente, setModalEditPaciente] = useState(false);
    const [modalEditState, setModalEditState] = useState(initialEditState);

    const [overBooking, setOverBooking] = useState(false);
    // Texto y botón del aviso de aforo («Pilates (martes 18:00) tendría 5 pacientes y su aforo es 4…»)
    const [overBookingAviso, setOverBookingAviso] = useState<{ texto: string; boton: string }>({ texto: '', boton: '' });
    const [onConfirmOverBooking, setOnConfirmOverBooking] = useState<() => void>(() => {});

    // Dar de baja a un paciente activo: se cierra su tramo en el último día que viene (se conserva el histórico)
    const [baja, setBaja] = useState<{ paciente: IPaciente; master: IMasterClass; tramo: IMasterClass['pacientes'][number]; ultimoDia: Date } | null>(null);
    const [modalDeletePaciente, setModalDeletePaciente] = useState(false);
    const [pacienteToDelete, setPacienteToDelete] = useState<{ paciente: Pick<IPaciente, '_id' | 'nombre' | 'apellidos'>; master: IMasterClass; tramo: IMasterClass['pacientes'][number] } | null>(null);

    const handleCloseAlert = (_?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
          return;
        }
        setAlert({ open: false, severity: 'info', msg: '' });
    };

    const resetAddModal = () => {
        // Desde la ficha no hay paso de selección al que volver
        if (props.masterIds) return props.onClose();
        setModalAddState(initialAddState);
        setModalAddPacientes(false);
    };

    const setModalAddField = (field: keyof typeof modalAddState, value: any) => {
        setModalAddState(prev => ({ ...prev, [field]: value }));
    };

    const resetEditModal = () => {
        setModalEditState(initialEditState);
        setModalEditPaciente(false);
    };

    const validateDates = (start: Date, end: Date, master: IMasterClass, originalStart?: Date): { valid: boolean, msg?: string } => {
        // Fecha vacía o a medio escribir: con una fecha no válida todas las comparaciones de abajo dan falso y se daba por buena
        // (el paciente quedaba apuntado con fechas vacías y sin clases)
        if (!esFechaValida(start) || !esFechaValida(end)) {
            return { valid: false, msg: 'Indica unas fechas de inicio y final válidas' };
        }
        const startMoment = moment(start).startOf('day');
        const endMoment = moment(end).endOf('day');
        const masterStartMoment = moment(master.start).startOf('day');
        const masterEndMoment = moment(master.terminarRepetir).endOf('day');
        const today = moment().startOf('day');
        const mismoInicio = !!originalStart && moment(originalStart).isSame(startMoment, 'day');

        if (startMoment.isAfter(endMoment)) {
            return { valid: false, msg: 'La fecha de inicio no puede ser posterior a la fecha final' };
        }

        // Un tramo de un solo día no es una inscripción: para una sesión suelta se apunta en esa clase desde la Agenda
        if (startMoment.isSame(endMoment, 'day')) {
            return { valid: false, msg: 'Para una sola clase, añádelo desde la Agenda en esa sesión' };
        }

        if (!mismoInicio) {
            if (startMoment.isBefore(today)) {
                return { valid: false, msg: 'La fecha de inicio no puede ser anterior a hoy' };
            }
            if (startMoment.isBefore(masterStartMoment)) {
                return { valid: false, msg: `La fecha de inicio no puede ser anterior al inicio de las clases: ${masterStartMoment.format('DD/MM/YYYY')}` };
            }
        }

        if (endMoment.isAfter(masterEndMoment)) {
            return { valid: false, msg: `La fecha final no puede ser mayor que el final de las clases: ${masterEndMoment.format('DD/MM/YYYY')}` };
        }

        // Al menos un día de clase en el rango (si no, el paciente quedaría apuntado sin ninguna sesión). weekDay: 0 = lunes
        let hayClase = false;
        for (const dia = startMoment.clone(); dia.isSameOrBefore(endMoment, 'day') && !hayClase; dia.add(1, 'day')) {
            hayClase = (dia.day() + 6) % 7 === master.weekDay;
        }
        if (!hayClase) {
            return { valid: false, msg: `No hay ninguna clase entre esas fechas: las clases son los ${constantes.weekNames[master.weekDay].toLowerCase()}` };
        }

        return { valid: true };
    };

    const getPacientesActivos = (
      pacientes: { id: string; start: Date; end: Date; _id?: string }[],
      selectedStart: Date,
      selectedEnd: Date
    ): { id: string; start: Date; end: Date; _id?: string }[] => {
      const start = moment(selectedStart).startOf('day');
      const end = moment(selectedEnd).endOf('day');
      const today = moment().startOf('day');

      //Nos quedamos con la última ocurrencia de cada paciente
      const uniqueById = pacientes.reduce((map, p) => {
        map.set(p.id, p); // sobrescribe -> se queda con el último
        return map;
      }, new Map<string, { id: string; start: Date; end: Date; _id?: string }>());

      const uniquePacientes = Array.from(uniqueById.values());

      // Filtrar pacientes que realmente ocupan hueco en el rango seleccionado
      // y que aún no hayan terminado
      return uniquePacientes.filter(p => {
        const pStart = moment(p.start).startOf('day');
        const pEnd = moment(p.end).endOf('day');

        return pEnd.isSameOrAfter(start) && pStart.isSameOrBefore(end) && pEnd.isSameOrAfter(today);
      });
    };

    // «Pilates (martes 18:00)»: la clase en los textos de los diálogos
    const claseCorta = (master: IMasterClass) =>
        `${actividades.find(a => a.internalID?.toString() === master.title.toString())?.title ?? ''} (${constantes.weekNames[master.weekDay].toLowerCase()} ${moment(master.start).format('H:mm')})`;

    // Día local elegido, como el mismo día en UTC (así se guardan los tramos)
    const diaUTC = (fecha: Date) => moment.utc({ year: fecha.getFullYear(), month: fecha.getMonth(), date: fecha.getDate() }).toDate();

    // Tramo de un paciente en la clase que se solapa (por días) con el rango dado
    const tramoSolapado = (pacientes: { id: string; start: Date; end: Date }[], pacienteId: string, start: Date, end: Date) =>
        pacientes.find(p =>
            p.id === pacienteId &&
            moment.utc(p.start).startOf('day').isSameOrBefore(moment.utc(end).endOf('day')) &&
            moment.utc(p.end).endOf('day').isSameOrAfter(moment.utc(start).startOf('day'))
        );

    // Último día que se puede elegir en «Entre dos fechas»: el final más tardío de las clases elegidas (cada una se valida con el suyo)
    const finesElegidas = checked.map(id => props.masterClasses.find(m => m._id === id)?.terminarRepetir).filter((f): f is Date => !!f).map(f => moment(f));
    const finMasElegidas = finesElegidas.length > 0 ? moment.max(finesElegidas) : undefined;

    // «Desde hoy hasta el final del curso» o «Entre dos fechas» (al volver a pulsar la opción elegida no cambia nada)
    const cambiarModoFechas = (value: string | null) => {
        if (!value) return;
        setModalAddState(prev => ({
            ...prev,
            allClasses: value,
            startClass: new Date(),
            endClass: new Date()
        }));
    };

    const onChangePacientesIds = (pacientesIds: IPaciente[]) => {
        const pacientes = [...pacientesIdsMultiple];
        for (let i = 0; i < checked.length; i++)
        {
            pacientes[i] = pacientesIds;
        }
        setPacientesIdsMultiple(pacientes);
    };

    const addPacientesToClass = (validateCapacity: boolean) => {
        // También si se eligieron pacientes y luego se quitaron todos del buscador (quedan listas vacías)
        if (pacientesIdsMultiple.every(lista => !lista || lista.length === 0)) {
            setAlert({ open: true, severity: 'warning', msg: 'Debes seleccionar al menos un paciente' });
            return;
        }
        
        let modifiedMasters: IMasterClass[] = [];
        let capacidadSuperada: boolean = false;

        for (let index = 0; index < checked.length; index++) {
            const master = props.masterClasses.find(x => x._id === checked[index])!;
            if (!master) continue;

            // «Desde hoy hasta el final del curso»: desde hoy o, si la serie aún no ha empezado, desde su primer día
            const selectedStart = modalAddState.allClasses === "Todas las clases"
                ? (moment(master.start).isAfter(moment(), 'day') ? new Date(master.start) : new Date())
                : modalAddState.startClass;
            const selectedEnd = modalAddState.allClasses === "Todas las clases" ? new Date(master.terminarRepetir) : modalAddState.endClass;

            const start = moment.utc({
                year: selectedStart.getFullYear(),
                month: selectedStart.getMonth(),
                date: selectedStart.getDate(),
            }).startOf('day').toDate();

            const end = moment.utc({
                year: selectedEnd.getFullYear(),
                month: selectedEnd.getMonth(),
                date: selectedEnd.getDate(),
            }).endOf('day').toDate();

            const validation = validateDates(selectedStart, selectedEnd, master);
            if (!validation.valid) return setAlert({ open: true, severity: 'warning', msg: validation.msg! });

            const masterPacientes = (pacientesIdsMultiple[index] ?? []).map(p => ({
                id: p._id,
                start,
                end
            }));

            const pacientesNormalized = master.pacientes.map(p => ({
                ...p,
                start: moment.utc(p.start).startOf('day').toDate(),
                end: moment.utc(p.end).endOf('day').toDate()
            }));

            // Un paciente no puede estar dos veces en la misma clase en fechas que se solapan
            for (const nuevo of masterPacientes) {
                const tramo = tramoSolapado(pacientesNormalized, nuevo.id, start, end);
                if (!tramo) continue;
                const paciente = (pacientesIdsMultiple[index] ?? []).find(p => p._id === nuevo.id);
                const actividad = actividades.find(a => a.internalID?.toString() === master.title.toString())?.title;
                return setAlert({ open: true, severity: 'warning', msg: `${paciente?.nombre} ${paciente?.apellidos} ya está en la clase de ${actividad} (${ClaseUtils.formatClassTitle(master)}) del ${ClaseUtils.formatUTCDate(tramo.start)} al ${ClaseUtils.formatUTCDate(tramo.end)}` });
            }

            if (validateCapacity) {
                const capacity = actividades.find(a => a.internalID?.toString() === master.title.toString())?.capacity ?? 0;

                const pacientesActivos = getPacientesActivos(
                    pacientesNormalized,
                    start,
                    end
                );

                const totalPacientes = pacientesActivos.length + masterPacientes.length;
                if (totalPacientes > capacity) {
                    setOnConfirmOverBooking(() => () => addPacientesToClass(false));
                    setOverBookingAviso({ texto: `${claseCorta(master)} tendría ${totalPacientes} pacientes y su aforo es ${capacity}. ¿Quieres añadirlos igualmente?`, boton: 'Añadir igualmente' });
                    setOverBooking(true);
                    capacidadSuperada = true;
                    return;
                }
            }
            // Copia: cambiar la clase de las props hacía que, al confirmar la capacidad o pulsar dos veces Guardar, se volvieran
            // a añadir los pacientes a las clases ya recorridas
            modifiedMasters.push({ ...master, pacientes: [...pacientesNormalized, ...masterPacientes] });
        }
    
        if (!capacidadSuperada) {
            setLoading(true);
            
            AgendaService.modifyPacientes(modifiedMasters)
                .then((response: { data: IMasterClass[] }) => {
                    // Al añadir se cierra y se vuelve a la ficha o al calendario
                    props.callBack(response.data);
                    props.onClose();
                })
                .catch((e: any) => {
                    if (e.response.status === 401) {
                        localStorage.clear();
                        history(constantes.R_HOME);
                    } else {
                        // Con error no se cierra nada: se avisa aquí y se puede corregir
                        const error: ILog = { method: 'PacientesActivities.Component.modifyPacientes', level: 'error', message: e.message, meta: e.response };
                        LogService.logError(error);
                        setAlert({ open: true, severity: 'error', msg: e.response?.data?.message || 'Error al modificar los pacientes de las clases' });
                    }
                })
                .finally(() => setLoading(false));
        }
    };    

    const addPacientes = () => {
        if (checked.length === 0) {
            setAlert({ open: true, severity: 'warning', msg: 'No se han seleccionado clases a modificar' });
            return;
        }

        const actividadIds: number[] = [];
        const weekdays: number[] = [];
        let horario: number = 0;

        checked.map((c) => {
            let master: IMasterClass = props.masterClasses.filter(x => x._id === c)[0];
            actividadIds.push(parseInt(master.title));
            weekdays.push(master.weekDay);
            if (new Date(master.start).getHours() < 15) horario = 1
            else horario = 2;
        });

        const actividadIdsUnicos =  actividadIds.filter((value, index, self) => self.indexOf(value) === index);
        const weekdaysUnicos = weekdays.filter((value, index, self) => self.indexOf(value) === index);

        AgendaService.getListaEsperaFiltered(actividadIdsUnicos, weekdaysUnicos, horario)
        .then((response: any) => {
            setModalAddField('listaEspera', response.data);
        })
        .catch((e: any) => {
            if (e.response.status === 401) {
                localStorage.clear();
                history(constantes.R_HOME);
            } else {
                const error: ILog = { method: 'PacientesActivities.Component.getListaEsperaFiltered', level: 'error', message: e.message, meta: e.response };
                LogService.logError(error);
                setAlert({ open: true, severity: 'error', msg: e.response.data.message });
            }
        })
        .finally(() => {
            setModalAddPacientes(true);
        });
    };

    // Desde la ficha de una clase las clases ya vienen elegidas: se abre directamente el paso de añadir pacientes
    useEffect(() => {
        if (props.masterIds) addPacientes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const editPaciente = (validateCapacity: boolean) => {
        if (!modalEditState.pacienteToEdit || !modalEditState.masterToEdit) return;
        let capacidadSuperada: boolean = false;

        const selectedStart = moment.utc({
            year: modalEditState.startClassEdit.getFullYear(),
            month: modalEditState.startClassEdit.getMonth(),
            date: modalEditState.startClassEdit.getDate(),
        }).startOf('day').toDate();

        const selectedEnd = moment.utc({
            year: modalEditState.endClassEdit.getFullYear(),
            month: modalEditState.endClassEdit.getMonth(),
            date: modalEditState.endClassEdit.getDate(),
        }).endOf('day').toDate();

        const validation = validateDates(modalEditState.startClassEdit, modalEditState.endClassEdit, modalEditState.masterToEdit, modalEditState.tramoToEdit?.start);
        if (!validation.valid) return setAlert({ open: true, severity: 'warning', msg: validation.msg! });

        // Sin solaparse con otro tramo suyo en esta clase (la API también lo rechaza, pero así se avisa sin enviar)
        const otroTramo = tramoSolapado(modalEditState.masterToEdit.pacientes.filter(t => t !== modalEditState.tramoToEdit), modalEditState.pacienteToEdit._id, selectedStart, selectedEnd);
        if (otroTramo) {
            return setAlert({ open: true, severity: 'warning', msg: `${modalEditState.pacienteToEdit.nombre} ${modalEditState.pacienteToEdit.apellidos} ya está en esta clase del ${ClaseUtils.formatUTCDate(otroTramo.start)} al ${ClaseUtils.formatUTCDate(otroTramo.end)}` });
        }

        // Copia también de los tramos: se cambian abajo y no deben tocar la clase de las props
        const masterCopy: IMasterClass = { ...modalEditState.masterToEdit, pacientes: modalEditState.masterToEdit.pacientes.map(p => ({ ...p })) };

        // Se cambia el tramo de la fila pulsada (antes siempre el último del paciente, aunque tuviera otro)
        const index = modalEditState.tramoToEdit ? modalEditState.masterToEdit.pacientes.indexOf(modalEditState.tramoToEdit) : -1;

        if (index !== -1) {
            masterCopy.pacientes[index].start = selectedStart;
            masterCopy.pacientes[index].end = selectedEnd;
        }

        const pacientesNormalized = masterCopy.pacientes.map(p => ({
            ...p,
            start: moment.utc(p.start).startOf('day').toDate(),
            end: moment.utc(p.end).endOf('day').toDate()
        }));

        if (validateCapacity) {
            const capacity = actividades.find(a => a.internalID?.toString() === masterCopy.title.toString())?.capacity ?? 0;

            const pacientesActivos = getPacientesActivos(pacientesNormalized, selectedStart, selectedEnd);

            if (pacientesActivos.length > capacity) {
                setOnConfirmOverBooking(() => () => editPaciente(false));
                setOverBookingAviso({ texto: `${claseCorta(masterCopy)} tendría ${pacientesActivos.length} pacientes en esas fechas y su aforo es ${capacity}. ¿Quieres guardar igualmente?`, boton: 'Guardar igualmente' });
                setOverBooking(true);
                capacidadSuperada = true;
                return;
            }
        }

        if (!capacidadSuperada) {
            setLoading(true);
            masterCopy.pacientes = pacientesNormalized;
            AgendaService.modifyPacientes([masterCopy])
                .then((response: { data: IMasterClass[] }) => {
                    // Vuelve a la lista de pacientes (el padre recarga las clases y la lista se actualiza sola)
                    resetEditModal();
                    props.callBack(response.data);
                })
                .catch((e: any) => {
                    if (e.response.status === 401) {
                        localStorage.clear();
                        history(constantes.R_HOME);
                    } else {
                        // Con error no se cierra nada: se avisa aquí y se puede corregir
                        const error: ILog = { method: 'PacientesActivities.Component.editPaciente', level: 'error', message: e.message, meta: e.response };
                        LogService.logError(error);
                        setAlert({ open: true, severity: 'error', msg: e.response?.data?.message || 'Error al modificar los pacientes de las clases' });
                    }
                })
                .finally(() => setLoading(false));
        }
    };

    // Si el paciente ya tiene un tramo que se solapa con las fechas elegidas en alguna de las clases, un texto que lo indica
    // («ya apuntado hasta 16/11/2026»). No se oculta del buscador: si se elige igualmente, el aviso sale al pulsar «Añadir».
    const yaApuntado = (paciente: IPaciente): string | undefined => {
        for (const id of checked) {
            const master = props.masterClasses.find(x => x._id === id);
            if (!master) continue;
            const todas = modalAddState.allClasses === "Todas las clases";
            const start = diaUTC(todas ? new Date() : modalAddState.startClass);
            const end = diaUTC(todas ? new Date(master.terminarRepetir) : modalAddState.endClass);
            const tramo = tramoSolapado(master.pacientes, paciente._id, start, end);
            if (!tramo) continue;
            const dia = checked.length > 1 ? ` el ${constantes.weekNames[master.weekDay].toLowerCase()}` : '';
            const empezado = moment.utc(tramo.start).startOf('day').isSameOrBefore(moment.utc().startOf('day'));
            const hastaElFinal = Utilities.compareDates(moment.utc(tramo.end).startOf('day').toDate(), moment.utc(master.terminarRepetir).startOf('day').toDate());
            if (empezado) return hastaElFinal ? `ya apuntado${dia}` : `ya apuntado${dia} hasta ${ClaseUtils.formatUTCDate(tramo.end)}`;
            return `ya apuntado${dia} desde ${ClaseUtils.formatUTCDate(tramo.start)}`;
        }
        return undefined;
    };

    const formatListaEspera = (): string => {  
        const result = modalAddState.listaEspera.map(lista1 => {
            const paciente = props.pacientes.find(p => p._id === lista1.pacienteId);
            const nombreCompleto = paciente ? `${paciente.nombre} ${paciente.apellidos}` : 'Paciente eliminado';
        
            let actividadesTitle = lista1.actividadIds.map(id => {
                const actividad = actividades.find(a => a.internalID?.toString() === id.toString());
                return actividad ? actividad.title : '';
            }).join(', ');
        
            let weekDaysTitle = lista1.weekdays.sort((a, b) => a - b).map(id => constantes.weekNames[id]).join(', ');
        
            const horario = constantes.horarioClases[lista1.horarioId].toLowerCase();
            const comentarios = lista1.comentarios ? lista1.comentarios : '';
        
            return `${nombreCompleto} - ${actividadesTitle} - ${weekDaysTitle} de ${horario} - ${comentarios}`;
        }).join('\n');

        return result;
    };

    const darDeBaja = () => {
        if (!baja) return;
        const { paciente, master, tramo, ultimoDia } = baja;
        if (!esFechaValida(ultimoDia)) return setAlert({ open: true, severity: 'warning', msg: 'Indica una fecha válida' });

        const dia = moment(ultimoDia).startOf('day');
        if (dia.isBefore(moment().startOf('day'))) return setAlert({ open: true, severity: 'warning', msg: 'El último día no puede ser anterior a hoy' });
        // Mismo día local, guardado como día UTC (así se guardan los tramos)
        const fin = moment.utc({ year: ultimoDia.getFullYear(), month: ultimoDia.getMonth(), date: ultimoDia.getDate() }).endOf('day');
        if (fin.isAfter(moment.utc(tramo.end).endOf('day'))) {
            return setAlert({ open: true, severity: 'warning', msg: `${paciente.nombre} ya termina el ${ClaseUtils.formatUTCDate(tramo.end)}` });
        }

        // Solo cambia el final de este tramo; el resto de tramos se envían igual (normalizados como en el resto de cambios)
        const pacientes = master.pacientes.map(t => ({
            ...t,
            start: moment.utc(t.start).startOf('day').toDate(),
            end: t === tramo ? fin.toDate() : moment.utc(t.end).endOf('day').toDate()
        }));

        setLoading(true);
        AgendaService.modifyPacientes([{ ...master, pacientes }])
            .then((response: { data: IMasterClass[] }) => {
                // Se queda abierto: el padre vuelve a cargar las clases y esta lista se actualiza sola
                setBaja(null);
                props.callBack(response.data);
            })
            .catch((e: { message?: string; response?: { status?: number; data?: { message?: string } } }) => {
                if (e.response?.status === 401) {
                    localStorage.clear();
                    history(constantes.R_HOME);
                } else {
                    const error: ILog = { method: 'PacientesActivities.Component.darDeBaja', level: 'error', message: e.message ?? '', meta: e.response };
                    LogService.logError(error);
                    setAlert({ open: true, severity: 'error', msg: e.response?.data?.message || 'Error al dar de baja al paciente' });
                }
            })
            .finally(() => setLoading(false));
    };

    const deletePacienteFromMasterClass = () => { 
        AgendaService.deletePacienteFromMasterClass(pacienteToDelete?.master._id!, pacienteToDelete?.paciente._id!)
        .then((response: { data: IMasterClass }) => {
            // Se queda abierto: el padre vuelve a cargar las clases y esta lista se actualiza sola
            setModalDeletePaciente(false);
            setPacienteToDelete(null);
            props.callBack([response.data]); // también muestra el aviso de que se ha guardado
        })
        .catch((e: any) => {
            if (e.response.status === 401) {
                localStorage.clear();
                history(constantes.R_HOME);
            } else {
                const error: ILog = { method: 'PacientesActivities.Component.deletePacienteFromMasterClass', level: 'error', message: e.message, meta: e.response };
                LogService.logError(error);
                setAlert({ open: true, severity: 'error', msg: e.response.data.message });
            }
        });
    };

    try {

        return (
            <>
                {/* SELECCION DE CLASES */}
                <Dialog open={!props.masterIds} maxWidth="md" fullWidth fullScreen={pantallaMovil} onClose={props.onClose}>
                    <DialogTitle sx={{ pb: 0 }}>Elige las clases donde modificar pacientes</DialogTitle>
                    <DialogContent>
                        <SelectorClases masterClasses={props.masterClasses} sesionesPorClase={props.sesionesPorClase} checked={checked} onChange={setChecked} />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={props.onClose}>Cancelar</Button>
                        <Button variant="contained" onClick={addPacientes}>{checked.length > 0 ? `Continuar (${checked.length})` : 'Continuar'}</Button>
                    </DialogActions>
                </Dialog>

                {/* AÑADIR PACIENTES */}
                <Dialog open={modalAddPacientes} maxWidth="md" fullWidth fullScreen={pantallaMovil}>
                    <DialogTitle sx={{ pb: 0 }}>
                        {(() => {
                            // Sin filter(Boolean): el código de la primera actividad es 0
                            const titulos = Array.from(new Set(checked.map(id => props.masterClasses.find(x => x._id === id)?.title).filter(codigo => codigo !== undefined)
                                .map(codigo => actividades.find(a => a.internalID?.toString() === String(codigo))?.title ?? '')));
                            return titulos.length === 1 ? `Pacientes de ${titulos[0]}` : 'Pacientes de las clases';
                        })()}
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Los cambios de fechas y las bajas se guardan al momento</Typography>

                        {/* Una tarjeta por clase, ordenadas por día y hora */}
                        <Grid container spacing={2}>
                        {
                            checked
                                .map(id => props.masterClasses.find(x => x._id === id))
                                .filter((m): m is IMasterClass => m !== undefined)
                                .sort((a, b) => a.weekDay - b.weekDay || moment(a.start).format('HH:mm').localeCompare(moment(b.start).format('HH:mm')))
                                .map(master => {
                                const today = moment.utc().startOf('day');
                                const actividad = actividades.find(a => a.internalID?.toString() === master.title.toString());
                                const capacidad = actividad?.capacity ?? 0;

                                const pacientesActivos = master.pacientes.filter(p => {
                                    const start = moment.utc(p.start).startOf('day');
                                    const end = moment.utc(p.end).endOf('day');
                                    return end.isSameOrAfter(today) && start.isSameOrBefore(today);
                                });

                                const pacienteNoActivos = master.pacientes.filter(p =>
                                    moment.utc(p.start).startOf('day').isAfter(today)
                                );
                                // Ocupación hoy o, si la clase aún no ha empezado, en su primer día (si no, saldría siempre vacía)
                                // Primera clase real de la serie (las sesiones ya vienen sin festivos); sin sesiones cargadas, su inicio guardado
                                const sesiones = props.sesionesPorClase?.[master._id!];
                                const primeraClase = ClaseUtils.primeraSesion(sesiones, new Date(master.start)) ?? new Date(master.start);
                                const empiezaMasAdelante = moment(primeraClase).isAfter(moment(), 'day');
                                const ref = empiezaMasAdelante ? moment(primeraClase) : moment();
                                const diaRef = moment.utc({ year: ref.year(), month: ref.month(), date: ref.date() });
                                const ocupados = new Set(master.pacientes
                                    .filter(p => moment.utc(p.start).startOf('day').isSameOrBefore(diaRef) && moment.utc(p.end).endOf('day').isSameOrAfter(diaRef))
                                    .map(p => p.id)).size;
                                const especialista = ConfigUtils.getEmpleados().find(e => e.internalID === master.especialistaId)?.firstName;
                                const salas: ISala[] = ConfigUtils.getSalas();
                                const sala = salas.find(s => String(s.internalID) === String(master.salaId))?.title;
                                const fin = moment(master.start).add(master.duracion, 'minutes');

                                // Activos y, después, los que empiezan más adelante, en una sola lista
                                const filas = [
                                    ...pacientesActivos.map(p => ({ tramo: p, futuro: false })),
                                    ...pacienteNoActivos.map(p => ({ tramo: p, futuro: true }))
                                ];

                                return (
                                    <Grid item xs={12} md={checked.length > 1 ? 6 : 12} key={master._id}>
                                        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5, height: '100%' }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography sx={{ fontWeight: 500 }}>
                                                    {`${constantes.weekNames[master.weekDay]} ${moment(master.start).format('H:mm')} a ${fin.format('H:mm')}`}
                                                </Typography>
                                                <Typography>{`${ocupados}/${capacidad}`}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                                                <Typography variant="body2" color="text.secondary">{[especialista, sala, `hasta ${moment(master.terminarRepetir).format('DD/MM/YYYY')}`].filter(Boolean).join(' · ')}</Typography>
                                                {empiezaMasAdelante && <Chip label={`empieza ${moment(primeraClase).format('DD/MM/YYYY')}`} size="small" color="info" variant="outlined" sx={{ height: 20 }} />}
                                            </Box>
                                            <LinearProgress
                                                variant="determinate"
                                                color={ocupados > capacidad ? 'warning' : 'primary'}
                                                value={capacidad > 0 ? Math.min(100, ocupados / capacidad * 100) : 0}
                                                sx={{ height: 6, borderRadius: 3, my: 1 }}
                                            />

                                            {filas.length === 0 && <Typography variant="body2" color="text.secondary">Sin pacientes</Typography>}
                                            {filas.map(({ tramo: p, futuro }, i) => {
                                                const paciente: IPaciente | undefined = props.pacientes.find(x => x._id === p.id);
                                                const nombrePaciente = paciente ? `${paciente.nombre} ${paciente.apellidos}` : 'Paciente eliminado';
                                                const hastaElFinal: boolean = Utilities.compareDates(moment.utc(p.end).startOf('day').toDate(), moment.utc(master.terminarRepetir).startOf('day').toDate());
                                                // Su primera clase real desde el inicio de su tramo (puede ser posterior por festivos o por el día de la semana)
                                                const primeraDelTramo = ClaseUtils.primeraSesion(sesiones, ClaseUtils.diaDeTramo(p.start));
                                                const inicioTexto = primeraDelTramo && moment(primeraDelTramo).isSameOrBefore(ClaseUtils.diaDeTramo(p.end), 'day')
                                                    ? moment(primeraDelTramo).format('DD/MM/YYYY')
                                                    : ClaseUtils.formatUTCDate(p.start);
                                                const etiqueta = futuro
                                                    ? (hastaElFinal ? `empieza ${inicioTexto}` : `${inicioTexto} - ${ClaseUtils.formatUTCDate(p.end)}`)
                                                    : (hastaElFinal ? '' : `hasta ${ClaseUtils.formatUTCDate(p.end)}`);
                                                return (
                                                    <Box key={`${p.id}-${i}`} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.25, borderTop: i > 0 ? '1px solid' : 'none', borderColor: 'divider' }}>
                                                        <Typography variant="body2" component="div">
                                                            {nombrePaciente}
                                                            {etiqueta && (futuro
                                                                ? <Chip label={etiqueta} size="small" color="info" variant="outlined" sx={{ ml: 1, height: 20 }} />
                                                                : <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>{etiqueta}</Typography>)}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', flexShrink: 0 }}>
                                                            {paciente && <Tooltip title={`Cambiar fechas de ${paciente.nombre}`}>
                                                                <IconButton size="small" onClick={() => {
                                                                    setModalEditState({
                                                                        pacienteToEdit: paciente,
                                                                        masterToEdit: master,
                                                                        tramoToEdit: p,
                                                                        startClassEdit: moment.utc(p.start).startOf('day').toDate(),
                                                                        endClassEdit: moment.utc(p.end).startOf('day').toDate()
                                                                    });
                                                                    setModalEditPaciente(true);
                                                                }}>
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>}
                                                            {/* Activos: baja desde un día (conserva el histórico). Los que aún no han empezado: se quitan */}
                                                            {!futuro && paciente && <Tooltip title={`Dar de baja a ${paciente.nombre}`}>
                                                                <IconButton size="small" onClick={() => setBaja({ paciente, master, tramo: p, ultimoDia: new Date() })}>
                                                                    <PersonRemoveIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>}
                                                            {futuro && <Tooltip title={`Quitar a ${paciente?.nombre ?? 'paciente eliminado'}`}>
                                                                <IconButton size="small" onClick={() => {
                                                                    setPacienteToDelete({ paciente: paciente ?? { _id: p.id, nombre: 'Paciente', apellidos: 'eliminado' }, master, tramo: p });
                                                                    setModalDeletePaciente(true);
                                                                }}>
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>}
                                                        </Box>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    </Grid>
                                )
                            })
                        }
                        </Grid>

                        {/* AÑADIR */}
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="h6" sx={{ mb: 1 }}>Añadir pacientes</Typography>

                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            color="primary"
                            orientation={pantallaMovil ? 'vertical' : 'horizontal'}
                            fullWidth={pantallaMovil}
                            value={modalAddState.allClasses}
                            onChange={(_, valor: string | null) => cambiarModoFechas(valor)}
                            sx={{ mt: 1 }}
                        >
                            <ToggleButton value="Todas las clases" sx={{ textTransform: 'none' }}>Desde hoy hasta el final del curso</ToggleButton>
                            <ToggleButton value="Seleccionar fechas" sx={{ textTransform: 'none' }}>Entre dos fechas</ToggleButton>
                        </ToggleButtonGroup>

                        {
                            modalAddState.allClasses === "Seleccionar fechas" &&

                            <Grid container spacing={1} sx={{ mt: 0.5 }}>
                                <Grid item xs={6} md={3}>
                                    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={locale}>
                                        <DatePicker
                                            renderInput={(props: any) => <TextField required fullWidth {...props} />}
                                            label="Fecha de inicio"
                                            minDate={moment()}
                                            maxDate={finMasElegidas}
                                            toolbarFormat="dd-mm-yyyy"
                                            value={modalAddState.startClass}
                                            onChange={(date: Moment | null) => setModalAddState(prev => ({ ...prev, startClass: diaDelSelector(date, 'inicio') }))}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={locale}>
                                        <DatePicker
                                            renderInput={(props: any) => <TextField required fullWidth {...props} />}
                                            label="Fecha final"
                                            minDate={moment()}
                                            maxDate={finMasElegidas}
                                            toolbarFormat="dd-mm-yyyy"
                                            value={modalAddState.endClass}
                                            onChange={(date: Moment | null) => setModalAddState(prev => ({ ...prev, endClass: diaDelSelector(date, 'inicio') }))}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                            </Grid>
                        }

                        {/* Primero cuándo y después a quién: el buscador indica quién ya está apuntado en esas fechas */}
                        <Autocomplete
                            disablePortal
                            autoComplete
                            id="pacientes"
                            multiple
                            options={props.pacientes}
                            noOptionsText={'No se han encontrado pacientes'}
                            renderOption={(props, option) => {
                                const apuntado = yaApuntado(option);
                                return (<li {...props} key={option._id}>{`${option.nombre} ${option.apellidos}`}{apuntado && <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>{`· ${apuntado}`}</Typography>}</li>);
                            }}
                            renderInput={(params) => <TextField {...params} label="Buscar pacientes" variant="outlined" margin="dense" fullWidth />}
                            onChange={(event, value) => { onChangePacientesIds(value || []) }}
                            getOptionLabel={(option) => `${option.nombre} ${option.apellidos}`}
                            filterOptions={(options, params) => Utilities.filterPacientesAutocomplete(options, params.inputValue)}
                        />

                        {modalAddState.listaEspera.length > 0 &&
                            <Alert
                                severity="info"
                                style={{ whiteSpace: 'pre-line', marginTop: 15 }}
                                action={
                                    <Button color="inherit" size="small" onClick={() => setModalAddState(prev => ({ ...prev, verListaEspera: !prev.verListaEspera }))}>
                                        {modalAddState.verListaEspera ? "OCULTAR" : "VER"}
                                    </Button>
                                }
                            >{modalAddState.verListaEspera ? formatListaEspera() : "Hay pacientes interesados en la lista de espera"}
                            </Alert>
                        }

                    </DialogContent>
                    {loading ?
                        <Grid container>
                            <CircularProgress style={{ margin: '20px auto' }} />
                        </Grid> :
                        <DialogActions>
                            <Button onClick={resetAddModal}>Cerrar</Button>
                            <Button variant="contained" onClick={() => addPacientesToClass(true)}>Añadir</Button>
                        </DialogActions>
                    }
                </Dialog>

                {/* EDITAR PACIENTE */}
                <Dialog open={modalEditPaciente} maxWidth="sm" fullWidth>
                    <DialogTitle sx={{ pb: 0 }}>{`Cambiar fechas de ${modalEditState.pacienteToEdit?.nombre ?? ''} ${modalEditState.pacienteToEdit?.apellidos ?? ''}`}</DialogTitle>
                    <DialogContent>
                        <><DialogContentText sx={{ mb: 1 }}>{modalEditState.masterToEdit ? `Fechas en ${claseCorta(modalEditState.masterToEdit)}` : ''}</DialogContentText>
                        
                        <Grid container item md={12} spacing={1}>
                            <Grid item md={4} sx={{ marginTop: 1 }}>
                                <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={locale}>
                                    <DatePicker
                                        renderInput={(props: any) => <TextField required fullWidth {...props} />}
                                        label="Fecha inicio"
                                        maxDate={modalEditState.masterToEdit ? moment(modalEditState.masterToEdit.terminarRepetir) : undefined}
                                        toolbarFormat="dd-mm-yyyy"
                                        value={modalEditState.startClassEdit}
                                        onChange={(date: Moment | null) => setModalEditState(prev => ({ ...prev, startClassEdit: diaDelSelector(date, 'inicio') }))}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item md={4} sx={{ marginTop: 1 }}>
                                <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={locale}>
                                    <DatePicker
                                        renderInput={(props: any) => <TextField required fullWidth {...props} />}
                                        label="Fecha final"
                                        minDate={esFechaValida(modalEditState.startClassEdit) ? moment(modalEditState.startClassEdit) : undefined}
                                        maxDate={modalEditState.masterToEdit ? moment(modalEditState.masterToEdit.terminarRepetir) : undefined}
                                        toolbarFormat="dd-mm-yyyy"
                                        value={modalEditState.endClassEdit}
                                        onChange={(date: Moment | null) => setModalEditState(prev => ({ ...prev, endClassEdit: diaDelSelector(date, 'fin') }))}
                                    />
                                </LocalizationProvider>
                            </Grid>
                        </Grid>
                        {/* La fecha guardada puede no ser día de clase (festivo, otro día de la semana): se indica la primera real */}
                        {(() => {
                            const { masterToEdit, tramoToEdit } = modalEditState;
                            if (!masterToEdit || !tramoToEdit) return null;
                            const inicio = ClaseUtils.diaDeTramo(tramoToEdit.start);
                            const primera = ClaseUtils.primeraSesion(props.sesionesPorClase?.[masterToEdit._id!], inicio);
                            if (!primera || moment(primera).isSame(inicio, 'day') || moment(primera).isAfter(ClaseUtils.diaDeTramo(tramoToEdit.end), 'day')) return null;
                            return <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{`Primera clase: ${moment(primera).format('DD/MM/YYYY')}`}</Typography>;
                        })()}
                        </>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => editPaciente(true)}>Guardar</Button>
                        <Button onClick={resetEditModal}>Cancelar</Button>
                    </DialogActions>
                </Dialog>

                {/* DAR DE BAJA A UN PACIENTE ACTIVO */}
                <Dialog open={baja !== null} maxWidth="xs" fullWidth onClose={() => setBaja(null)}>
                    <DialogTitle>{`Dar de baja a ${baja?.paciente.nombre} ${baja?.paciente.apellidos}`}</DialogTitle>
                    <DialogContent>
                        <DialogContentText sx={{ mb: 2 }}>
                            {baja && `Clase de ${actividades.find(a => a.internalID?.toString() === baja.master.title.toString())?.title} (${ClaseUtils.formatClassTitle(baja.master)}). Seguirá apuntado hasta el último día que indiques; su historial se conserva.`}
                        </DialogContentText>
                        <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={locale}>
                            <DatePicker
                                renderInput={(props: any) => <TextField required fullWidth {...props} />}
                                label="Último día que viene"
                                minDate={moment()}
                                maxDate={baja ? moment.utc(baja.tramo.end).local(true) : undefined}
                                toolbarFormat="dd-mm-yyyy"
                                value={baja?.ultimoDia ?? null}
                                onChange={(date: Moment | null) => setBaja(prev => prev ? { ...prev, ultimoDia: diaDelSelector(date, 'inicio') } : prev)}
                            />
                        </LocalizationProvider>
                    </DialogContent>
                    {loading ?
                        <Grid container>
                            <CircularProgress style={{ margin: '20px auto' }} />
                        </Grid> :
                        <DialogActions>
                            <Button onClick={darDeBaja} color="error">Dar de baja</Button>
                            <Button onClick={() => setBaja(null)}>Cancelar</Button>
                        </DialogActions>
                    }
                </Dialog>

                {/* ELIMINAR PACIENTE */}
                <Dialog open={modalDeletePaciente} maxWidth="sm" fullWidth  onClose={() => {setModalDeletePaciente(false); setPacienteToDelete(null);}}>
                    <DialogTitle>{`Quitar a ${pacienteToDelete?.paciente?.nombre ?? ''} ${pacienteToDelete?.paciente?.apellidos ?? ''} de la clase`}</DialogTitle>
                    <>
                        <DialogContent>
                            <DialogContentText>
                              {pacienteToDelete && (() => {
                                  // Desde su primera clase real (las sesiones ya vienen sin festivos)
                                  const desde = ClaseUtils.primeraSesion(props.sesionesPorClase?.[pacienteToDelete.master._id!], ClaseUtils.diaDeTramo(pacienteToDelete.tramo.start));
                                  const fecha = desde ? moment(desde).format('DD/MM/YYYY') : ClaseUtils.formatUTCDate(pacienteToDelete.tramo.start);
                                  return `Se quitará su inscripción en ${claseCorta(pacienteToDelete.master)} desde el ${fecha}. Sus otras fechas en esta clase, si las tiene, no cambian.`;
                              })()}
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => {setModalDeletePaciente(false); setPacienteToDelete(null);}} autoFocus>Cancelar</Button>
                            <Button onClick={deletePacienteFromMasterClass} color="error">Quitar</Button>
                        </DialogActions>
                    </>
                </Dialog>

                {/* CONFIRMAR CAPACIDAD */}
                <Dialog open={overBooking} maxWidth="xs" fullWidth onClose={() => setOverBooking(false)}>
                    <DialogTitle>Aforo superado</DialogTitle>
                    <>
                        <DialogContent>
                            <DialogContentText>{overBookingAviso.texto}</DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setOverBooking(false)} autoFocus>Cancelar</Button>
                            <Button onClick={() => { onConfirmOverBooking(); setOverBooking(false); }} color="warning">{overBookingAviso.boton}</Button>
                        </DialogActions>
                    </>
                </Dialog>

                <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
                    <Alert variant="filled" onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>{alert.msg}</Alert>
                </Snackbar>
            </>
        );
    } catch (e: any) {
        const error: ILog = { method: 'PacienteActivities.Component', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
        return (
            <Alert severity="error">{e.message}</Alert>
        );
    }

};

export default PacienteActivities;